import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, errMsg } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token. Use the exact link from your email.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((res) => {
        setState("success");
        setMessage(res.data.message);
      })
      .catch((err) => {
        setState("error");
        setMessage(errMsg(err));
      });
  }, [token]);

  return (
    <AuthLayout>
      <Card className="border shadow-none">
        <CardContent className="pt-8 pb-8 text-center">
          {state === "verifying" && (
            <div className="py-6 flex flex-col items-center gap-4" data-testid="verify-email-loading">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Verifying your email…</p>
            </div>
          )}
          {state === "success" && (
            <div data-testid="verify-email-success">
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--status-ok)/0.12)]">
                <CheckCircle2 className="h-6 w-6 text-[hsl(var(--status-ok))]" />
              </div>
              <h1 className="text-xl font-semibold">Email verified</h1>
              <p className="mt-3 text-sm text-muted-foreground">You're all set. Log in to open your dashboard.</p>
              <Link to="/login" className="block mt-6">
                <Button className="w-full rounded-xl" data-testid="verify-email-login-button">Log in</Button>
              </Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="verify-email-error">
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--status-overdue)/0.12)]">
                <XCircle className="h-6 w-6 text-[hsl(var(--status-overdue))]" />
              </div>
              <h1 className="text-xl font-semibold">Link didn't work</h1>
              <p className="mt-3 text-sm text-muted-foreground">{message}</p>
              <Link to="/login" className="block mt-6">
                <Button variant="secondary" className="w-full rounded-xl" data-testid="verify-email-back-login-button">Back to log in</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
